import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { invalidateLocations } from "../../lib/locations";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, CaretRight, CaretDown, MapTrifold, Buildings,
  GlobeHemisphereWest, Tree, House, UploadSimple, Download, FileCsv,
} from "@phosphor-icons/react";

const NEXT_TYPE = { state: "district", district: "lsgd", lsgd: "ward", ward: null };
const ICON = { state: GlobeHemisphereWest, district: Buildings, lsgd: Tree, ward: House };
const LSGD_TYPES = [
  { value: "corporation", label: "Corporation" },
  { value: "municipality", label: "Municipality" },
  { value: "grama_panchayat", label: "Grama Panchayat" },
];

function flatten(nodes, depth, out, expanded) {
  for (const n of nodes) {
    out.push({ ...n, _depth: depth });
    if (expanded[n.id] !== false && n.children && n.children.length > 0) {
      flatten(n.children, depth + 1, out, expanded);
    }
  }
  return out;
}

const emptyForm = {
  mode: "create", type: "state", id: null, name: "",
  state_id: "", district_id: "", lsgd_id: "",
  lsgd_type: "grama_panchayat", ward_no: "",
};

export default function Locations() {
  const [tree, setTree] = useState([]);
  const [flatAll, setFlatAll] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [csvOpen, setCsvOpen] = useState(null); // "district" | "lsgd" | "ward" | null
  const [csvFile, setCsvFile] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  const load = async () => {
    try {
      invalidateLocations();
      const [t, all] = await Promise.all([
        api.get("/locations/tree"),
        api.get("/locations"),
      ]);
      setTree(t.data);
      setFlatAll(all.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const states = useMemo(() => flatAll.filter((x) => x.type === "state"), [flatAll]);
  const districts = useMemo(
    () => flatAll.filter((x) => x.type === "district" && (!form.state_id || x.parent_id === form.state_id)),
    [flatAll, form.state_id],
  );
  const lsgds = useMemo(
    () => flatAll.filter((x) => x.type === "lsgd" && (!form.district_id || x.parent_id === form.district_id)),
    [flatAll, form.district_id],
  );

  const flat = flatten(tree, 0, [], expanded);

  const openCreate = (type) => {
    setSelected(null);
    setForm({ ...emptyForm, mode: "create", type });
  };

  const startEdit = () => {
    if (!selected) return;
    const anc = ancestors(selected);
    setForm({
      mode: "edit",
      type: selected.type,
      id: selected.id,
      name: selected.name,
      state_id: anc.state || "",
      district_id: anc.district || "",
      lsgd_id: anc.lsgd || "",
      lsgd_type: selected.lsgd_type || "grama_panchayat",
      ward_no: selected.ward_no || "",
    });
  };

  const ancestors = (node) => {
    const map = {};
    let cur = node;
    const byId = Object.fromEntries(flatAll.map((x) => [x.id, x]));
    while (cur) {
      map[cur.type] = cur.id;
      cur = cur.parent_id ? byId[cur.parent_id] : null;
    }
    return map;
  };

  const doDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.name}"?`)) return;
    try {
      await api.delete(`/locations/${selected.id}`);
      toast.success("Deleted");
      await load();
      setForm(emptyForm);
      setSelected(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async (e) => {
    e.preventDefault();
    let parent_id = null;
    if (form.type === "district") parent_id = form.state_id;
    else if (form.type === "lsgd") parent_id = form.district_id;
    else if (form.type === "ward") parent_id = form.lsgd_id;

    if (form.type !== "state" && !parent_id) {
      toast.error(`Please select a ${{ district: "state", lsgd: "district", ward: "LSGD" }[form.type]}`);
      return;
    }
    try {
      const payload = {
        name: form.name, type: form.type, parent_id,
        lsgd_type: form.type === "lsgd" ? form.lsgd_type : undefined,
        ward_no: form.type === "ward" ? (form.ward_no || null) : undefined,
      };
      if (form.mode === "edit") {
        await api.put(`/locations/${form.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/locations", payload);
        toast.success(`${form.type} created`);
      }
      await load();
      setForm((f) => ({ ...f, name: "", ward_no: "" }));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadTemplate = async (type) => {
    const token = localStorage.getItem("rescigo_token");
    const r = await fetch(`${API_BASE}/locations/template?type=${type}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) { toast.error("Download failed"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `locations_${type}_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const uploadCsv = async () => {
    if (!csvFile) { toast.error("Pick a CSV file"); return; }
    setCsvBusy(true);
    setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const r = await api.post(`/locations/import?type=${csvOpen}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setCsvResult(r.data);
      toast.success(`Imported · ${r.data.created} new, ${r.data.skipped} skipped`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setCsvBusy(false);
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: e[id] === false ? true : false }));

  return (
    <AppShell
      title="Location Hierarchy"
      subtitle="Master Control"
      actions={
        <div className="flex gap-2">
          <button data-testid="btn-import-ward" onClick={() => { setCsvOpen("ward"); setCsvFile(null); setCsvResult(null); }}
                  className="h-10 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <UploadSimple size={12} weight="bold" />CSV import
          </button>
          <button data-testid="btn-add-state" onClick={() => openCreate("state")}
                  className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
            <Plus size={14} weight="bold" /> Add State
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tree */}
        <div className="lg:col-span-2 bg-white border border-[#E5E2D9] rounded-md">
          <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Tree view</div>
              <div className="font-heading text-lg">State → District → LSGD → Ward</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => openCreate("district")} data-testid="btn-add-district"
                      className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs flex items-center gap-1.5 hover:bg-[#F7F6F2]">
                <Plus size={12} />District
              </button>
              <button onClick={() => openCreate("lsgd")} data-testid="btn-add-lsgd"
                      className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs flex items-center gap-1.5 hover:bg-[#F7F6F2]">
                <Plus size={12} />LSGD
              </button>
              <button onClick={() => openCreate("ward")} data-testid="btn-add-ward"
                      className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs flex items-center gap-1.5 hover:bg-[#F7F6F2]">
                <Plus size={12} />Ward
              </button>
              <button data-testid="btn-edit" disabled={!selected} onClick={startEdit}
                      className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs flex items-center gap-1.5 hover:bg-[#F7F6F2] disabled:opacity-40">
                <PencilSimple size={12} /> Edit
              </button>
              <button data-testid="btn-delete" disabled={!selected} onClick={doDelete}
                      className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs text-[#C25D38] flex items-center gap-1.5 hover:bg-[#C25D38]/8 disabled:opacity-40">
                <Trash size={12} /> Delete
              </button>
            </div>
          </div>

          <div className="p-4 min-h-[500px]" data-testid="tree-root">
            {flat.length === 0 && (
              <div className="py-20 text-center text-sm text-[#8A8780]">No locations yet. Start by adding a State.</div>
            )}
            {flat.map((n) => {
              const Icon = ICON[n.type] || MapTrifold;
              const isSel = selected?.id === n.id;
              const hasChildren = n.children && n.children.length > 0;
              const isOpen = expanded[n.id] !== false;
              const metaTxt = n.type === "lsgd" && n.lsgd_type
                ? n.lsgd_type.replace("_", " ")
                : n.type === "ward" && n.ward_no
                  ? `No. ${n.ward_no}`
                  : n.type;
              return (
                <div
                  key={n.id}
                  data-testid={`node-${n.id}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${isSel ? "bg-[#D4E0D1]" : "hover:bg-[#EFEDE5]"}`}
                  style={{ marginLeft: n._depth * 16 }}
                  onClick={() => setSelected(n)}
                >
                  {hasChildren ? (
                    <button onClick={(e) => { e.stopPropagation(); toggle(n.id); }} className="text-[#8A8780] p-0.5">
                      {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    </button>
                  ) : (<span className="w-[22px]" />)}
                  <Icon size={14} color="#2D5A27" weight="duotone" />
                  <div className="flex-1 text-sm text-[#1A1A1A] truncate">{n.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A8780] capitalize">{metaTxt}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-6" data-testid="location-form">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">
            {form.mode === "edit" ? "Edit location" : "Create new"}
          </div>
          <div className="font-heading text-lg mb-4 capitalize">
            {form.mode === "edit" ? `Editing "${form.name || "…"}"` : `New ${form.type}`}
          </div>

          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Type</label>
              <select
                data-testid="input-type"
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...emptyForm, mode: f.mode, id: f.id, type: e.target.value }))}
                disabled={form.mode === "edit"}
                className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white disabled:bg-[#F7F6F2] capitalize"
              >
                {["state", "district", "lsgd", "ward"].map((t) => (
                  <option key={t} value={t}>{t.toUpperCase()}</option>
                ))}
              </select>
            </div>

            {form.type === "district" && (
              <Select label="State *" testid="sel-state" value={form.state_id}
                      onChange={(v) => setForm((f) => ({ ...f, state_id: v }))}
                      options={states} />
            )}
            {form.type === "lsgd" && (
              <>
                <Select label="State *" testid="sel-state" value={form.state_id}
                        onChange={(v) => setForm((f) => ({ ...f, state_id: v, district_id: "" }))}
                        options={states} />
                <Select label="District *" testid="sel-district" value={form.district_id}
                        onChange={(v) => setForm((f) => ({ ...f, district_id: v }))}
                        options={districts} disabled={!form.state_id} />
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">LSGD type *</label>
                  <select data-testid="sel-lsgd-type" value={form.lsgd_type}
                          onChange={(e) => setForm((f) => ({ ...f, lsgd_type: e.target.value }))}
                          className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                    {LSGD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </>
            )}
            {form.type === "ward" && (
              <>
                <Select label="State *" testid="sel-state" value={form.state_id}
                        onChange={(v) => setForm((f) => ({ ...f, state_id: v, district_id: "", lsgd_id: "" }))}
                        options={states} />
                <Select label="District *" testid="sel-district" value={form.district_id}
                        onChange={(v) => setForm((f) => ({ ...f, district_id: v, lsgd_id: "" }))}
                        options={districts} disabled={!form.state_id} />
                <Select label="LSGD *" testid="sel-lsgd" value={form.lsgd_id}
                        onChange={(v) => setForm((f) => ({ ...f, lsgd_id: v }))}
                        options={lsgds} disabled={!form.district_id} />
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Ward no.</label>
                  <input data-testid="input-ward-no" value={form.ward_no}
                         onChange={(e) => setForm((f) => ({ ...f, ward_no: e.target.value }))}
                         placeholder="e.g. 1 or 12A"
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono" />
                </div>
              </>
            )}

            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">
                {form.type === "ward" ? "Ward name *" : "Name *"}
              </label>
              <input data-testid="input-name" required value={form.name}
                     onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                     className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
            </div>

            <button data-testid="btn-save-location" type="submit"
                    className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
              {form.mode === "edit" ? "Save changes" : "Create"}
            </button>
          </form>

          {/* CSV quick download */}
          <div className="mt-6 pt-6 border-t border-[#E5E2D9]">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Bulk import templates</div>
            <div className="space-y-2">
              {["district", "lsgd", "ward"].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <button onClick={() => downloadTemplate(t)} data-testid={`tpl-${t}`}
                          className="h-8 px-2.5 text-xs border border-[#E5E2D9] rounded text-[#2D5A27] inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
                    <Download size={11} />Download template
                  </button>
                  <button onClick={() => { setCsvOpen(t); setCsvFile(null); setCsvResult(null); }}
                          data-testid={`imp-${t}`}
                          className="h-8 px-2.5 text-xs border border-[#E5E2D9] rounded inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
                    <UploadSimple size={11} />Import
                  </button>
                  <span className="text-[11px] text-[#8A8780] capitalize">{t}s</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CSV import modal */}
      {csvOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setCsvOpen(null)}>
          <div className="bg-white w-full max-w-md rounded-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <FileCsv size={20} color="#2D5A27" weight="fill" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">CSV import</div>
                <div className="font-heading text-lg capitalize">{csvOpen}s</div>
              </div>
            </div>
            <div className="text-xs text-[#5C5A55] bg-[#F7F6F2] border border-[#E5E2D9] rounded p-3 mb-4">
              Need the format? <button onClick={() => downloadTemplate(csvOpen)} className="text-[#2D5A27] underline">Download template</button>.
              Existing rows are skipped (matched by name + parent).
            </div>
            <input type="file" accept=".csv,text/csv" data-testid="csv-file"
                   onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                   className="w-full text-sm mb-4" />
            {csvResult && (
              <div className="text-xs bg-[#D4E0D1]/40 border border-[#D4E0D1] rounded p-3 mb-4" data-testid="csv-result">
                Created: <b>{csvResult.created}</b> · Skipped: <b>{csvResult.skipped}</b> · Errors: <b>{csvResult.errors?.length || 0}</b>
                {csvResult.errors?.length > 0 && (
                  <ul className="mt-2 space-y-0.5 list-disc pl-4">
                    {csvResult.errors.slice(0, 5).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                  </ul>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setCsvOpen(null)} className="flex-1 h-10 border border-[#E5E2D9] rounded-md text-sm">Close</button>
              <button onClick={uploadCsv} disabled={csvBusy || !csvFile} data-testid="csv-upload"
                      className="flex-1 h-10 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                <UploadSimple size={12} weight="bold" />{csvBusy ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

const Select = ({ label, testid, value, onChange, options, disabled }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{label}</label>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white disabled:bg-[#F7F6F2] disabled:opacity-60">
      <option value="">{disabled ? "— select parent first —" : "— select —"}</option>
      {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  </div>
);
